import React, { useCallback, useMemo } from "react";

import * as Select from "@radix-ui/react-select";
import { AHSeparator, AHText, Box, useLayoutContext, useTheme } from "arkh";
import styles from "./TimezoneSelector.module.css";
import { useIntl } from "react-intl";
import { capitalizeFirstLetter } from "../../../utils";

const allLangs = [
  "en",
  "sv",
  "de",
  "no",
  "fi",
  "bg",
  "fr",
  "pl",
  "cs",
  "hr",
  "pt",
  "da",
  "hu",
  "ro",
  "is",
  "ru",
  "et",
  "it",
  "sk",
  "el",
  "lt",
  "sl",
  "es",
  "lv",
  "nl",
] as const;

type K = (typeof allLangs)[number];

const defValues: K[] = ["en", "sv", "de", "no", "fi"];
const emojis: Record<K, string> = {
  en: "🇬🇧",
  sv: "🇸🇪",
  de: "🇩🇪",
  no: "🇧🇻",
  fi: "🇫🇮",
  bg: "🇧🇬",
  fr: "🇫🇷",
  pl: "🇵🇱",
  cs: "🇨🇿",
  hr: "🇭🇷",
  pt: "🇵🇹",
  da: "🇩🇰",
  hu: "🇭🇺",
  ro: "🇲🇩",
  is: "🇮🇸",
  ru: "🇷🇺",
  et: "🇪🇪",
  it: "🇮🇹",
  sk: "🇸🇰",
  el: "🇬🇷",
  lt: "🇱🇹",
  sl: "🇸🇮",
  es: "🇪🇸",
  lv: "🇱🇻",
  nl: "🇳🇱",
};

export const LanguageSelector = React.memo(LanguageSelectorC);
export function LanguageSelectorC(props: {
  selectedLanguage: string;
  onValueChange: (v: string) => void;
}) {
  const layout = useLayoutContext();
  const theme = useTheme();
  const intl = useIntl();

  const dn = useMemo(
    () => new Intl.DisplayNames(intl.locale, { type: "language" }),
    [intl.locale]
  );
  const formatLanguage = useCallback(
    (lang: string) => capitalizeFirstLetter(dn.of(lang) ?? lang),
    [dn]
  );
  const otherValues = useMemo(
    () => allLangs.filter((s) => defValues.indexOf(s) === -1),

    []
  );

  const val = allLangs.find((s) => s === props.selectedLanguage) ?? "en";
  return (
    <Select.Root
      value={props.selectedLanguage}
      onValueChange={(v) => {
        props.onValueChange(v as string);
      }}
    >
      <Select.Trigger
        className={styles.Trigger}
        style={{
          cursor: "pointer",
          // backgroundColor: theme.surface,
          border: `1px solid ${theme["border-primary"]}`,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: layout.radius.large,
          paddingLeft: layout.space.medium,
          paddingTop: layout.space.medium,
          paddingBottom: layout.space.medium,
          paddingRight: layout.space.small,
          lineHeight: 1,
          height: 50,
        }}
      >
        <Select.Value>
          <Box
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",

              flex: 1,
            }}
          >
            <AHText spaceRight="small" style={{ flex: 1 }} color="text-primary">
              {emojis[val]} {formatLanguage(val)}
            </AHText>
          </Box>
        </Select.Value>
      </Select.Trigger>

      <Select.Portal>
        <Select.Content
          style={{
            cursor: "pointer",
            overflow: "hidden",
            backgroundColor: theme.surface,
            borderRadius: layout.radius.medium,
            zIndex: 99999,
            boxShadow:
              "0px 10px 38px -10px rgba(22, 23, 24, 0.35), 0px 10px 20px -15px rgba(22, 23, 24, 0.2)",
          }}
        >
          <Select.ScrollUpButton />
          <Select.Viewport>
            {defValues.map((p) => (
              <Select.Item
                key={p}
                value={p}
                className={styles.SelectItem}
                style={{
                  fontSize: 13,
                  lineHeight: 1,

                  borderRadius: 3,
                  display: "flex",
                  alignItems: "center",
                  padding: "15px 20px 15px 20px",
                  position: "relative",
                  userSelect: "none",
                }}
              >
                <Select.ItemText>
                  <AHText color="darkerText" spaceRight="large">
                    {emojis[p]} {formatLanguage(p)}
                  </AHText>
                </Select.ItemText>

                <Select.ItemIndicator />
              </Select.Item>
            ))}
            <AHSeparator />

            {otherValues.map((p) => (
              <Select.Item
                key={p}
                value={p}
                className={styles.SelectItem}
                style={{
                  fontSize: 13,
                  lineHeight: 1,

                  borderRadius: 3,
                  display: "flex",
                  alignItems: "center",
                  padding: "15px 20px 15px 20px",
                  position: "relative",
                  userSelect: "none",
                }}
              >
                <Select.ItemText>
                  <AHText color="darkerText" spaceRight="large">
                    {emojis[p]} {formatLanguage(p)}
                  </AHText>
                </Select.ItemText>

                <Select.ItemIndicator />
              </Select.Item>
            ))}

            <Select.Separator />
          </Select.Viewport>
          <Select.ScrollDownButton />
          <Select.Arrow />
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
